import { ReadonlyVec3 } from 'gl-matrix';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { IdField } from '~/components/Fields';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { DoseViewData } from '~/state/atoms/dose/doseAtoms';
import {
    dicomViewerAtom,
    showSensitiveInfoState,
} from '~/state/atoms/viewerAtoms';
import PropertiesPanel from './PropertiesPanel';

type DoseDisplayData = {
    sopUid: string;
    type: string;
    generated: boolean;
    doseScale: number;
    summationType?: string;
    doseGridResolution?: ReadonlyVec3;
    planLabel?: string;
    maxDoseGy?: number;
    manufacturer?: string;
    manufacturerModelName?: string;
    seriesDescription?: string;
    doseComment?: string;
    softwareVersions?: string;
    normalizationValueGy?: number;
    datasetWrapper?: DcmJsDatasetWrapper;
};

function toDoseDisplayData(dose: DoseMatrix | DoseViewData): DoseDisplayData {
    if (dose instanceof DoseMatrix) {
        const dicom = dose.dicomDose;
        return {
            sopUid: dose.sopInstanceUid,
            type: dose.doseType,
            generated: dicom.manufacturer === 'MVision AI',
            doseScale: dose.doseScale,
            summationType: dose.doseSummationType,
            doseGridResolution: dose.grid.getRes(),
            planLabel: dose.plan?.label,
            maxDoseGy: dose.doseMaxGy,
            manufacturer: dicom.manufacturer,
            manufacturerModelName: dicom.manufacturerModelName,
            seriesDescription: dicom.seriesDescription,
            doseComment: dicom.doseComment,
            softwareVersions: dicom.softwareVersions,
            normalizationValueGy: dose.normalizationValueGy,
            datasetWrapper: dicom.datasetWrapper,
        };
    }
    return {
        sopUid: dose.sopUid,
        type: dose.type,
        generated: dose.generated,
        doseScale: dose.doseScale,
        summationType: dose.summationType,
        doseGridResolution: dose.doseGridResolution,
        planLabel: dose.planLabel,
        maxDoseGy: dose.maxDoseGy,
        manufacturer: dose.manufacturer,
        manufacturerModelName: dose.manufacturerModelName,
        seriesDescription: dose.seriesDescription,
        doseComment: dose.doseComment,
        softwareVersions: dose.softwareVersions,
        normalizationValueGy: dose.normalizationValueGy,
    };
}

interface DosePropertiesProps {
    dose: DoseMatrix | DoseViewData;
    label?: string;
    showTitle?: boolean;
    onDelete?: () => void;
}

export default function DoseProperties({
    dose,
    label,
    showTitle = true,
    onDelete,
}: DosePropertiesProps) {
    const intl = useIntl();
    const setDicomViewer = useSetRecoilState(dicomViewerAtom);
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    const doseDisplayData = useMemo(() => toDoseDisplayData(dose), [dose]);

    const onViewDicom = useCallback(() => {
        if (!doseDisplayData.datasetWrapper) {
            return;
        }
        setDicomViewer({
            dataset: doseDisplayData.datasetWrapper,
            title: label ?? 'Dose',
        });
    }, [doseDisplayData.datasetWrapper, label, setDicomViewer]);

    const doseScaleDisplay =
        doseDisplayData.doseScale !== 1
            ? doseDisplayData.doseScale.toFixed(2)
            : null;
    const doseGridResolutionLabel = doseDisplayData.doseGridResolution
        ? `${doseDisplayData.doseGridResolution[0]} x ${doseDisplayData.doseGridResolution[1]} x ${doseDisplayData.doseGridResolution[2]}`
        : null;

    return (
        <PropertiesPanel
            showTitle={showTitle}
            onViewDicom={
                doseDisplayData.datasetWrapper ? onViewDicom : undefined
            }
            onDelete={onDelete}
        >
            <LabelText>
                <FormattedMessage id="doseType" />
            </LabelText>
            <ValueText
                component="span"
                title={
                    doseDisplayData.generated ? undefined : doseDisplayData.type
                }
            >
                {doseDisplayData.generated ? (
                    <FormattedMessage id="predicted" />
                ) : (
                    doseDisplayData.type
                )}
            </ValueText>

            {doseDisplayData.summationType && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseSummationType" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.summationType}
                    >
                        {doseDisplayData.summationType}
                    </ValueText>
                </>
            )}

            {doseGridResolutionLabel && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseGridResolutionMm" />
                    </LabelText>
                    <ValueText component="span" title={doseGridResolutionLabel}>
                        {doseGridResolutionLabel}
                    </ValueText>
                </>
            )}

            {doseDisplayData.planLabel && (
                <>
                    <LabelText>
                        <FormattedMessage id="description" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={
                            showSensitiveInfo
                                ? doseDisplayData.planLabel
                                : undefined
                        }
                    >
                        <SensitiveInfoTypography>
                            {doseDisplayData.planLabel}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            {doseDisplayData.maxDoseGy && (
                <>
                    <LabelText>
                        <FormattedMessage id="maxDose" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={`${doseDisplayData.maxDoseGy.toFixed(1)} Gy`}
                    >
                        {doseDisplayData.maxDoseGy.toFixed(1)} Gy
                    </ValueText>
                </>
            )}

            {doseDisplayData.manufacturer && (
                <>
                    <LabelText>
                        <FormattedMessage id="manufacturer" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.manufacturer}
                    >
                        {doseDisplayData.manufacturer}
                    </ValueText>
                </>
            )}

            {doseDisplayData.manufacturerModelName && (
                <>
                    <LabelText>
                        <FormattedMessage id="manufacturerModelName" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.manufacturerModelName}
                    >
                        {doseDisplayData.manufacturerModelName}
                    </ValueText>
                </>
            )}

            {doseDisplayData.seriesDescription && (
                <>
                    <LabelText>
                        <FormattedMessage id="seriesDescription" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={
                            showSensitiveInfo
                                ? doseDisplayData.seriesDescription
                                : undefined
                        }
                    >
                        <SensitiveInfoTypography>
                            {doseDisplayData.seriesDescription}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            {doseDisplayData.doseComment && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseComment" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.doseComment}
                    >
                        {doseDisplayData.doseComment}
                    </ValueText>
                </>
            )}

            {doseDisplayData.softwareVersions && (
                <>
                    <LabelText>
                        <FormattedMessage id="softwareVersions" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.softwareVersions}
                    >
                        {doseDisplayData.softwareVersions}
                    </ValueText>
                </>
            )}

            {doseDisplayData.normalizationValueGy != null && (
                <>
                    <LabelText>
                        <FormattedMessage id="normalization" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={`${doseDisplayData.normalizationValueGy.toFixed(1)} Gy`}
                    >
                        {doseDisplayData.normalizationValueGy.toFixed(1)} Gy
                    </ValueText>
                </>
            )}

            {doseScaleDisplay && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseScaledBy" />
                    </LabelText>
                    <ValueText component="span" title={doseScaleDisplay}>
                        {doseScaleDisplay}
                    </ValueText>
                </>
            )}

            <LabelText>
                {intl.formatMessage({ id: 'sopInstanceUid' })}
            </LabelText>
            <IdField
                id={doseDisplayData.sopUid}
                label={undefined}
                typographyProps={{ fontSize: 11 }}
            />
        </PropertiesPanel>
    );
}
